/**
 * GolferDetailsStep — Golfer profile details
 *
 * Step 2 (when skipVerification=true): Collects name, email, phone,
 * handicap, golf ID, and home club.
 *
 * - First/last name pre-populated from auth (mock: "Google User" etc.)
 * - Auth email shown as read-only; secondary email hidden behind toggle
 * - Phone is optional with tooltip (constrained within widget)
 * - GolfConnectID / Handicap Identifier field (optional)
 * - Home club optional
 */

import { useState } from 'preact/hooks'
import type { GolferDetailsData } from '../hooks/useBookingState'

interface GolferDetailsStepProps {
  onSubmit: (data: GolferDetailsData) => void
  initialEmail?: string
  authMethod?: string
  requirePhone?: boolean
}

/** Mock name from auth provider */
function getAuthName(method?: string): { first: string; last: string } {
  switch (method) {
    case 'google': return { first: 'James', last: 'Anderson' }
    case 'apple': return { first: 'James', last: 'A.' }
    case 'meta': return { first: 'James', last: 'Anderson' }
    default: return { first: '', last: '' }
  }
}

export function GolferDetailsStep({ onSubmit, initialEmail, authMethod, requirePhone }: GolferDetailsStepProps) {
  const authName = getAuthName(authMethod)
  const [firstName, setFirstName] = useState(authName.first)
  const [lastName, setLastName] = useState(authName.last)
  const [showSecondaryEmail, setShowSecondaryEmail] = useState(false)
  const [secondaryEmail, setSecondaryEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [golfId, setGolfId] = useState('')
  const [handicap, setHandicap] = useState('')
  const [homeClubName, setHomeClubName] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (!firstName.trim()) {
      setError('Please enter your first name')
      return
    }
    if (!lastName.trim()) {
      setError('Please enter your last name')
      return
    }
    setError('')
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: secondaryEmail.trim() || initialEmail || '',
      phone: phone.trim(),
      handicap: handicap.trim(),
      homeClubName: homeClubName.trim(),
      golfId: golfId.trim(),
    })
  }

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit()
  }

  const canSubmit = firstName.trim() && lastName.trim()

  return (
    <div className="bw2-golfer-details" role="form" aria-label="Golfer details">
      <div className="bw2-golfer-details__header">
        <h2 className="bw2-golfer-details__title">Your Details</h2>
        <div className="bw2-golfer-details__subtitle">
          Tell us a bit about yourself so we can personalise your experience
        </div>
      </div>

      <div className="bw2-golfer-details__form">
        {/* Name row — pre-populated from auth */}
        <div className="bw2-golfer-details__row">
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label" htmlFor="bw2-gd-firstName">
              First Name *
            </label>
            <input
              className="bw2-golfer-details__input"
              id="bw2-gd-firstName"
              type="text"
              value={firstName}
              onInput={e => { setFirstName((e.target as HTMLInputElement).value); setError('') }}
              onKeyDown={handleKeyPress}
              placeholder="James"
              autoComplete="given-name"
              aria-required="true"
              autoFocus
            />
          </div>
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label" htmlFor="bw2-gd-lastName">
              Last Name *
            </label>
            <input
              className="bw2-golfer-details__input"
              id="bw2-gd-lastName"
              type="text"
              value={lastName}
              onInput={e => { setLastName((e.target as HTMLInputElement).value); setError('') }}
              onKeyDown={handleKeyPress}
              placeholder="Anderson"
              autoComplete="family-name"
              aria-required="true"
            />
          </div>
        </div>

        {/* Auth email — read-only display */}
        {initialEmail && (
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label">
              Email
            </label>
            <div className="bw2-golfer-details__email-display">
              {initialEmail}
            </div>
            {!showSecondaryEmail && (
              <button
                className="bw2-golfer-details__add-email-link"
                onClick={() => setShowSecondaryEmail(true)}
                type="button"
              >
                + Add a secondary email
              </button>
            )}
          </div>
        )}

        {/* Secondary email — hidden by default */}
        {showSecondaryEmail && (
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label" htmlFor="bw2-gd-email2">
              Secondary Email{' '}
              <span className="bw2-golfer-details__optional">(optional)</span>
            </label>
            <input
              className="bw2-golfer-details__input"
              id="bw2-gd-email2"
              type="email"
              value={secondaryEmail}
              onInput={e => setSecondaryEmail((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyPress}
              placeholder="alternate@email.com"
              autoComplete="email"
            />
          </div>
        )}

        {/* Phone row — optional */}
        <div className="bw2-golfer-details__field">
          <label className="bw2-golfer-details__label" htmlFor="bw2-gd-phone">
            Phone Number{' '}
            <span className="bw2-golfer-details__optional">(optional)</span>
            <span className="bw2-golfer-details__tooltip">
              <span className="bw2-golfer-details__tooltip-icon">?</span>
              <span className="bw2-golfer-details__tooltip-text">
                We'll only use this to contact you about your booking — e.g. weather delays or course updates
              </span>
            </span>
          </label>
          <input
            className="bw2-golfer-details__input"
            id="bw2-gd-phone"
            type="tel"
            value={phone}
            onInput={e => setPhone((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyPress}
            placeholder="+61 4XX XXX XXX"
            autoComplete="tel"
          />
        </div>

        {/* Golf ID + Handicap row */}
        <div className="bw2-golfer-details__row">
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label" htmlFor="bw2-gd-golfId">
              Golf ID{' '}
              <span className="bw2-golfer-details__optional">(optional)</span>
              <span className="bw2-golfer-details__tooltip">
                <span className="bw2-golfer-details__tooltip-icon">?</span>
                <span className="bw2-golfer-details__tooltip-text">
                  GolfConnect ID (Australia) or Handicap Identifier (international)
                </span>
              </span>
            </label>
            <input
              className="bw2-golfer-details__input"
              id="bw2-gd-golfId"
              type="text"
              value={golfId}
              onInput={e => setGolfId((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g. 2012345678"
            />
          </div>
          <div className="bw2-golfer-details__field">
            <label className="bw2-golfer-details__label" htmlFor="bw2-gd-handicap">
              Handicap{' '}
              <span className="bw2-golfer-details__optional">(optional)</span>
            </label>
            <input
              className="bw2-golfer-details__input"
              id="bw2-gd-handicap"
              type="text"
              value={handicap}
              onInput={e => setHandicap((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g. 12"
            />
          </div>
        </div>

        {/* Home Club */}
        <div className="bw2-golfer-details__field">
          <label className="bw2-golfer-details__label" htmlFor="bw2-gd-homeClub">
            Home Club{' '}
            <span className="bw2-golfer-details__optional">(optional)</span>
          </label>
          <input
            className="bw2-golfer-details__input"
            id="bw2-gd-homeClub"
            type="text"
            value={homeClubName}
            onInput={e => setHomeClubName((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyPress}
            placeholder="Kingston Heath"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bw2-login__error" role="alert" style={{ marginTop: '4px' }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          className="bw2-login__submit-btn"
          onClick={handleSubmit}
          disabled={!canSubmit}
          type="button"
          style={{ marginTop: '4px' }}
        >
          Continue to Tee Times →
        </button>
      </div>
    </div>
  )
}
